import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, LogIn, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import { discordService } from '@/lib/services/discord.service';
import { Caixa } from '@/construtor/caixa';

type Estado = 'trabalhando' | 'login' | 'indisponivel' | 'erro';

/**
 * Onde o botão do Discord leva: começa o vínculo sem passar pelo perfil.
 *
 * Location: src/pages
 * Why: o botão da mensagem fixa (e do /vincular) é um link, igual para todo
 * mundo. Esta página faz o resto com a sessão de quem abriu: pede login se
 * faltar, manda quem já vinculou para o perfil e leva os outros direto à
 * autorização no Discord. O `state` continua nascendo na API, preso a esta
 * sessão, como no botão do perfil.
 */
export default function DiscordStartLinkPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { store } = useStore();
  const { isAuthenticated, isLoading, openLoginModal } = useAuth();
  const [estado, setEstado] = useState<Estado>('trabalhando');
  const iniciado = useRef(false);

  useEffect(() => {
    if (isLoading || !store?.id) return;
    if (!isAuthenticated) {
      setEstado('login');
      openLoginModal();
      return;
    }
    if (iniciado.current) return;
    iniciado.current = true;
    setEstado('trabalhando');

    (async () => {
      try {
        const meu = await discordService.getMine(store.id);
        if (meu?.link) {
          toast.success(t('store.discord.start_already', { username: meu.link.username }));
          navigate('/perfil?tab=discord', { replace: true });
          return;
        }
        if (!meu?.available) {
          setEstado('indisponivel');
          return;
        }
        const url = await discordService.start(store.id);
        // Só segue para o Discord. Qualquer outro destino seria a API respondendo errado.
        if (!url.startsWith('https://discord.com/')) throw new Error('destino inesperado');
        window.location.replace(url);
      } catch {
        iniciado.current = false;
        setEstado('erro');
      }
    })();
  }, [isLoading, isAuthenticated, store?.id, openLoginModal, navigate, t]);

  return (
    <div className="container mx-auto flex min-h-[60vh] items-center justify-center px-4 py-16">
      <Caixa id="perfil.discord.vincular" miolo>
      <Card className="glass-strong w-full max-w-md border-border/50">
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center" data-testid="discord-start-link">
          <h1 className="text-xl font-bold">{t('store.discord.start_title')}</h1>

          {estado === 'trabalhando' && (
            <p className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('store.discord.redirecting')}
            </p>
          )}
          {estado === 'login' && (
            <>
              <p className="text-muted-foreground">{t('store.discord.start_login', { store: store?.name ?? '' })}</p>
              <Button onClick={openLoginModal}>
                <LogIn className="mr-2 h-4 w-4" />
                {t('store.discord.start_login_button')}
              </Button>
            </>
          )}
          {estado === 'indisponivel' && <p className="text-muted-foreground">{t('store.discord.start_unavailable')}</p>}
          {estado === 'erro' && (
            <p className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5 shrink-0" />
              {t('store.discord.error')}
            </p>
          )}

          {(estado === 'indisponivel' || estado === 'erro') && (
            <Button asChild variant="outline">
              <Link to="/perfil?tab=discord">{t('store.discord.callback_back')}</Link>
            </Button>
          )}
        </CardContent>
      </Card>
      </Caixa>
    </div>
  );
}
